import React from 'react';
import Box from '@material-ui/core/Box';
import Table, {ColumnType, Sort} from '@pluto/table';
import {DateRangeState} from '@pluto/daterange-picker/DateRangeTypes';
import {PlutoSelectChange} from '@pluto/select';
import {HandleAutocompleteChange} from '@pluto/autocomplete';
import {UserValue} from '@pluto/users';
import {SummaryContainer as QCManagerSummary, Section} from '@pluto/common/summary';
import QCFilters from '../QCFilters/QCFiltersConnected';
import {QCSpecificFilters, QCStatusEnum} from '../QCFilters/QCFiltersType';
import {SectionTitles} from '../QCWorkFlowTypes';
import {
    QCJobItemModified,
    HandleChangeSelectType,
    HandleChangeInputType,
    HandlePreselectChange,
    onDatePickerChange,
    Assignee,
    SetDates,
    QCManagerState,
} from './QCManagerTypes';
import PriorityQCModal from './QCManagerDialogs/PriorityQCModal/PriorityQCModal';
import AssignModal from './QCManagerDialogs/AssignQCModal/AssignDialog';
import QCManagerJobsControlButtons from './QCManagerJobsControlButtons/QCManagerJobsControlButtons';
import './QCManager.scss';

const SCORE_BOARD_SECTIONS: Section[] = [
    {
        status: [QCStatusEnum.ReadyForQc],
        title: SectionTitles.ReadyForQc,
    },
    {
        status: [QCStatusEnum.InQc],
        title: SectionTitles.InQc,
    },
    {
        status: [QCStatusEnum.QcFailed],
        title: SectionTitles.QcFailed,
    },
    {
        status: [QCStatusEnum.QcPassed, QCStatusEnum.QcFailed],
        title: SectionTitles.QcCompleteTwo,
        complete: true,
    },
];

interface QCManagerProps {
    isAssigned: boolean;
    onMultipleJobDueDateChange: (dateValue: string) => void;
    handleSelectChangePriority: HandleChangeSelectType;
    setRowsPerPage: (value: number) => void;
    rowsPerPage: number;
    setPage: (value: number) => void;
    page: number;
    title: string;
    filters: QCSpecificFilters;
    getChecked: (value: number) => boolean;
    handlePreselect: HandlePreselectChange;
    handleStatusChange: PlutoSelectChange;
    handleAssigneeChange: HandleAutocompleteChange;
    handleContentPartnersChange: HandleAutocompleteChange;
    handleContentTypesChange: HandleAutocompleteChange;
    handleTypeChange: HandleChangeSelectType;
    handleTitleChange: HandleChangeInputType;
    handlePriorityChange: () => void;
    handlePriorityClose: () => void;
    isPriorityModalOpen: boolean;
    isAssignModalOpen: boolean;
    handleAssignClose: () => void;
    handleUserAssign: () => void;
    handleUserUnAssign: () => void;
    handleSelectChangeAssign: (value: Assignee | null | undefined) => void;
    onPaging: () => void;
    data: QCJobItemModified[];
    columns: ColumnType[];
    total: number;
    setSort: (newSort: Sort) => void;
    sort: Sort;
    onExport?: () => void;
    dueDates: DateRangeState;
    finishDates: DateRangeState;
    onDueDateRangeSelect: onDatePickerChange;
    onFinishDateRangeSelect: onDatePickerChange;
    setFinishDates: SetDates;
    handleMultiChangePriorityModal: () => void;
    handleMultiChangeAssigneeModal: () => void;
    checkedItems: number[];
    assignedUser: UserValue;
    qcManager: QCManagerState;
}

export default function QCManager(props: QCManagerProps) {
    const {
        data,
        columns,
        total,
        filters,
        onPaging,
        page,
        setPage,
        getChecked,
        handleSelectChangePriority,
        rowsPerPage,
        setRowsPerPage,
        title,
        handlePreselect,
        handleTypeChange,
        handleContentPartnersChange,
        handleContentTypesChange,
        handleAssigneeChange,
        handleStatusChange,
        handleTitleChange,
        handlePriorityChange,
        handlePriorityClose,
        isPriorityModalOpen,
        handleAssignClose,
        handleUserAssign,
        handleUserUnAssign,
        handleSelectChangeAssign,
        isAssignModalOpen,
        isAssigned,
        setSort,
        sort,
        // onExport,
        dueDates,
        finishDates,
        onDueDateRangeSelect,
        onFinishDateRangeSelect,
        setFinishDates,
        handleMultiChangePriorityModal,
        handleMultiChangeAssigneeModal,
        checkedItems,
        assignedUser,
        onMultipleJobDueDateChange,
        qcManager,
    } = props;

    return (
        <Box className="qc-manager">
            <QCManagerSummary
                status={filters.qcJobStatus}
                setFilter={handlePreselect}
                // onExport={onExport}
                setDates={setFinishDates}
                dates={dueDates}
                sections={SCORE_BOARD_SECTIONS}
                state={qcManager}
            />
            <AssignModal
                checkedItems={checkedItems}
                data={data}
                assignedUser={assignedUser}
                open={isAssignModalOpen}
                isAssigned={isAssigned}
                handleAssignClose={handleAssignClose}
                handleUserAssign={handleUserAssign}
                handleSelectChangeAssign={handleSelectChangeAssign}
            />
            <PriorityQCModal
                open={isPriorityModalOpen}
                checkedItems={checkedItems}
                data={data}
                getChecked={getChecked}
                handleSelectChangePriority={handleSelectChangePriority}
                handlePriorityChange={handlePriorityChange}
                handlePriorityClose={handlePriorityClose}
            />
            <QCFilters
                filters={filters}
                title={title}
                handleTitleChange={handleTitleChange}
                handleTypeChange={handleTypeChange}
                handleContentPartnersChange={handleContentPartnersChange}
                handleContentTypesChange={handleContentTypesChange}
                handleAssigneeChange={handleAssigneeChange}
                handleStatusChange={handleStatusChange}
                onDueDateRangeSelect={onDueDateRangeSelect}
                onFinishDateRangeSelect={onFinishDateRangeSelect}
                dueDates={dueDates}
                finishDates={finishDates}
            />

            <Table
                data={data}
                columns={columns}
                setSort={setSort}
                sort={sort}
                total={total}
                onChangePageHandler={onPaging}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
            >
                <QCManagerJobsControlButtons
                    onMultipleJobDueDateChange={onMultipleJobDueDateChange}
                    handleUserUnAssign={handleUserUnAssign}
                    handleMultiChangePriorityModal={handleMultiChangePriorityModal}
                    handleMultiChangeAssigneeModal={handleMultiChangeAssigneeModal}
                    checkedItems={checkedItems}
                    rowsLength={total}
                />
            </Table>
        </Box>
    );
}
